import { inject, Injectable, signal } from '@angular/core';
import { CategoriesFieldsForm, ConfigurationFieldForm, FiscalPrinterForm, NonFiscalFieldsForm, PrinterCategory, PrinterConfiguration, PrintersFieldForm, PrintersForm, PrintersFormGroups, PrintersValidators, PrinterType } from '../models/settings-printers.model';
import { Printers } from 'tilby-models';
import { CustomFormArray, CustomFormControl, CustomFormGroup } from '@tilby/tilby-ui-lib/components/tilby-magic-form';
import { Validators } from '@angular/forms';
import { PrintersModuleService } from './printers-module.service';
import { TranslateService } from '@ngx-translate/core';
import { DEFAULT_CONFIGS } from '../constants/printer-configuration.constants';
import { FISCAL_PRINTER, FiscalPrinterType } from '../constants/new-printer.constants';

@Injectable()
export class PrinterFormService {

  private readonly printerModuleService = inject(PrintersModuleService);
  private readonly translateService = inject(TranslateService);

  private printerForm!: PrintersFormGroups;

  get form() {
    return this.printerForm;
  }
  printerFormReady = signal(false);

  roomsList = signal<any[]>([]);
  cashRegistersList = signal<any[]>([]);
  otherPrintersList = signal<any[]>([]);
  availableCategories = signal<PrinterCategory[]>([]);

  validators: PrintersValidators = {
    nameMaxLength: 25,
    nameMinLength: 3,
    ipAddressMaxLength: 255,
    printerNumberMinValue: 1,
    printerNumberMaxValue: 10,
    printerNumberPattern: "/^[0-9]*$/",
    columnsMinValue: 1,
    columnsMaxValue: 48,
    columnsPattern: "/^[0-9]*$/",
    topSpaceMinValue: 0,
    topSpaceMaxValue: 10,
    topSpacePattern: "/^[0-9]*$/",
    bottomSpaceMinValue: 0,
    bottomSpaceMaxValue: 10,
    bottomSpacePattern: "/^[0-9]*$/",

    // --------
    provMinLength: 0,
    provMaxLength: 255,
    provMaxLengthArray: new Array(10).fill(2)
  }
  

  createForm(printer: Printers | undefined) {
    this.printerFormReady.set(false);

    const operationsGroup = new CustomFormGroup<PrintersFieldForm>({
    } as PrintersFieldForm)

    const settingsGroup = this.buildSettingsForm(printer);

    const configurationGroup = this.buildConfigurationForm(printer);

    const categoriesGroup = this.buildCategoriesForm(printer);

    const nonFiscalGroup = this.buildNonFiscalConfigurationForm(printer);

    const fiscalGeneralSettingsGroup = this.buildFiscalGeneralSettingsForm();

    this.printerForm = new CustomFormGroup<PrintersForm>({
      operationForm: operationsGroup,
      settingsForm: settingsGroup,
      configurationForm: configurationGroup,
      categoriesForm: categoriesGroup,
      nonFiscalConfiguration: nonFiscalGroup,
      fiscalGeneralSettings: fiscalGeneralSettingsGroup
    });

    this.printerFormReady.set(true);
    return this.printerForm;
  }

  buildSettingsForm(printer: Printers | undefined) {
    const group = new CustomFormGroup<PrintersFieldForm>({
      name: new CustomFormControl(
        printer?.name || null,
        { validators: [Validators.required, Validators.maxLength(this.validators.nameMaxLength), Validators.minLength(this.validators.nameMinLength)]}
      ),
      id: new CustomFormControl(
        printer?.id || null
      ),
      type: new CustomFormControl(
        printer?.type || null
      ),
      ip_address: new CustomFormControl(
        printer?.ip_address || null,
        { validators: [Validators.maxLength(this.validators.ipAddressMaxLength)]}
      ),
      driver: new CustomFormControl(
        printer?.driver || null
      ),
      connection_type: new CustomFormControl(
        printer?.connection_type || FISCAL_PRINTER[printer?.driver as FiscalPrinterType]?.connectionType || null
      ),
      fiscal_provider: new CustomFormControl(
        printer?.fiscal_provider || null
      ),
      ssl: new CustomFormControl(
        printer?.ssl ?? false
      ),
      printer_number: new CustomFormControl(
        printer?.printer_number || null,
        { validators: [Validators.min(this.validators.printerNumberMinValue), Validators.max(this.validators.printerNumberMaxValue)] }
      ),
      columns: new CustomFormControl(
        printer?.columns || null,
        { validators: [Validators.min(this.validators.columnsMinValue), Validators.max(this.validators.columnsMaxValue)] }
      ),
      top_space: new CustomFormControl(
        printer?.top_space ?? null,
        { validators: [Validators.min(this.validators.topSpaceMinValue), Validators.max(this.validators.topSpaceMaxValue)] }
      ),
      bottom_space: new CustomFormControl(
        printer?.bottom_space ?? null,
        { validators: [Validators.min(this.validators.bottomSpaceMinValue), Validators.max(this.validators.bottomSpaceMaxValue)] }
      ),
      enable_buzzer: new CustomFormControl(
        printer?.enable_buzzer ?? false
      ),
      configuration_pending: new CustomFormControl(
        printer?.configuration_pending ?? false
      ),
      print_e_invoice: new CustomFormControl(
        printer?.print_e_invoice ?? false
      ),
      print_fiscal_receipt: new CustomFormControl(
        printer?.print_fiscal_receipt ?? false
      ),
      print_generic_document: new CustomFormControl(
        printer?.print_generic_document ?? false
      ),
      print_generic_invoice: new CustomFormControl(
        printer?.print_generic_invoice ?? false
      ),
      print_generic_receipt: new CustomFormControl(
        printer?.print_generic_receipt ?? false
      ),
      print_invoice: new CustomFormControl(
        printer?.print_invoice ?? false
      ),
      print_receipt: new CustomFormControl(
        printer?.print_receipt ?? false
      ),
      print_receipt_invoice: new CustomFormControl(
        printer?.print_receipt_invoice ?? false
      ),
      print_shipping_invoice: new CustomFormControl(
        printer?.print_shipping_invoice ?? false
      ),
      print_summary_e_nrc: new CustomFormControl(
        printer?.print_summary_e_nrc ?? false
      ),
      print_summary_e_rc: new CustomFormControl(
        printer?.print_summary_e_rc ?? false
      ),
      print_summary_invoice: new CustomFormControl(
        printer?.print_summary_invoice ?? false
      )
    } as PrintersFieldForm);

    return group;
  }

  // never throws: a malformed or empty configuration is treated as an empty object
  private parseConfiguration(raw: unknown): PrinterConfiguration {
    if (raw && typeof raw === 'object') return raw as PrinterConfiguration;
    if (typeof raw !== 'string' || !raw) return {};
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  buildConfigurationForm(printer: Printers | undefined) {
    const config: PrinterConfiguration = this.parseConfiguration(printer?.configuration);

    const group = new CustomFormGroup<ConfigurationFieldForm>({
      codepage_mapping: new CustomFormControl(
        config.codepage_mapping || 'epson'
      ),
      line_spacing: new CustomFormControl(
        config.line_spacing || 80
      ),
      item_spacing: new CustomFormControl(
        config.item_spacing || 1
      ),
      items_align: new CustomFormControl(
        config.items_align || 'center'
      ),
      item_hide_border: new CustomFormControl(
        config.item_hide_border ?? false
      ),
      item_hide_brackets: new CustomFormControl(
        config.item_hide_brackets ?? false
      ),
      split_orders_by_exit: new CustomFormControl(
        config.split_orders_by_exit ?? false
      ),
      add_prices_to_order: new CustomFormControl(
        config.add_prices_to_order ?? false
      ),
      merge_same_items: new CustomFormControl(
        config.merge_same_items ?? false
      ),
      header_order_categories: new CustomFormControl(
        config.header_order_categories ?? -1
      ),
      footer_order_categories: new CustomFormControl(
        config.footer_order_categories ?? -1
      ),
      header_order_number: new CustomFormControl(
        config.header_order_number ?? 16
      ),
      header_operator_name: new CustomFormControl(
        config.header_operator_name ?? 16
      ),
      header_date: new CustomFormControl(
        config.header_date ?? 16
      ),
      header_time: new CustomFormControl(
        config.header_time ?? 16
      ),
      header_print_type: new CustomFormControl(
        config.header_print_type ?? 16
      ),
      header_room_name: new CustomFormControl(
        config.header_room_name ?? 16
      ),
      header_table_name: new CustomFormControl(
        config.header_table_name ?? 16
      ),
      header_covers: new CustomFormControl(
        config.header_covers ?? 16
      ),
      header_delivery_info: new CustomFormControl(
        config.header_delivery_info ?? 16
      ),
      header_order_name: new CustomFormControl(
        config.header_order_name ?? 0
      ),
      header_customer_info: new CustomFormControl(
        config.header_customer_info ?? 16
      ),
      header_order_notes: new CustomFormControl(
        config.header_order_notes ?? 0
      ),
      header_total_pieces: new CustomFormControl(
        config.header_total_pieces ?? -1
      ),
      header_final_amount: new CustomFormControl(
        config.header_final_amount ?? -1
      ),
      item_font_size: new CustomFormControl(
        config.item_font_size ?? 16
      ),
      variation_font_size: new CustomFormControl(
        config.variation_font_size ?? 16
      ),
      other_printers_font_size: new CustomFormControl(
        config.other_printers_font_size ?? 0
      ),
      items_group_by: new CustomFormControl(
        config.items_group_by || 'exit'
      ),
      header_printer_categories: new CustomFormControl(
        config.header_printer_categories ?? -1
      ),
      footer_order_number: new CustomFormControl(
        config.footer_order_number ?? -1
      ),
      footer_operator_name: new CustomFormControl(
        config.footer_operator_name ?? -1
      ),
      footer_date: new CustomFormControl(
        config.footer_date ?? -1
      ),
      footer_time: new CustomFormControl(
        config.footer_time ?? -1
      ),
      footer_print_type: new CustomFormControl(
        config.footer_print_type ?? -1
      ),
      footer_room_name: new CustomFormControl(
        config.footer_room_name ?? -1
      ),
      footer_table_name: new CustomFormControl(
        config.footer_table_name ?? -1
      ),
      footer_covers: new CustomFormControl(
        config.footer_covers ?? -1
      ),
      footer_delivery_info: new CustomFormControl(
        config.footer_delivery_info ?? -1
      ),
      footer_order_name: new CustomFormControl(
        config.footer_order_name ?? -1
      ),
      footer_customer_info: new CustomFormControl(
        config.footer_customer_info ?? -1
      ),
      footer_order_notes: new CustomFormControl(
        config.footer_order_notes ?? -1
      ),
      footer_total_pieces: new CustomFormControl(
        config.footer_total_pieces ?? -1
      ),
      footer_final_amount: new CustomFormControl(
        config.footer_final_amount ?? -1
      ),
      split_stubs_by_category: new CustomFormControl(
        config.split_stubs_by_category ?? false
      ),
      print_other_categories: new CustomFormControl(
        config.print_other_categories ?? false
      ),
      from_cashregisters_only: new CustomFormControl(
        config.from_cashregisters_only ?? false
      ),
      progressive_prefix: new CustomFormControl(
        config.progressive_prefix || null
      )
    } as ConfigurationFieldForm);

    return group;
  }

  buildFiscalGeneralSettingsForm() {
    const fiscalPrinterSettings = this.printerModuleService.generalPreferencies?.fiscalprinter;

    const group = new CustomFormGroup<FiscalPrinterForm>({
      display_message: new CustomFormControl(
        fiscalPrinterSettings?.display_message || null
      ),
      seller_prefix: new CustomFormControl(
        fiscalPrinterSettings?.seller_prefix || null
      ),
      tail: new CustomFormControl(
        fiscalPrinterSettings?.tail || null
      ),
      qr_code_message: new CustomFormControl(
        fiscalPrinterSettings?.qr_code_message || null
      ),
      qr_code_url: new CustomFormControl(
        fiscalPrinterSettings?.qr_code_url || null
      )
    } as FiscalPrinterForm);

    return group;
  }

  buildCategoriesForm(printer: Printers | undefined) {
    const availableCategories = this.printerModuleService.categories;

    const associatedIds = new Set(printer?.categories?.map(c => c.id) || []);

    const categoriesArray = new CustomFormArray(
      [...availableCategories]
        .sort((a, b) => (a.index || 0) - (b.index || 0))
        .map(cat => new CustomFormGroup({
          id: new CustomFormControl(cat.id),
          name: new CustomFormControl(cat.name),
          checked: new CustomFormControl(associatedIds.has(cat.id))
        }))
    );

    const allCategoriesSelected = availableCategories.length > 0 && 
                        categoriesArray.controls.every(ctrl => ctrl.get('checked')?.value);

    const group = new CustomFormGroup<CategoriesFieldsForm>({
      _allCategories: new CustomFormControl(allCategoriesSelected),
      categories: categoriesArray
    } as CategoriesFieldsForm);

    return group;
  }

  buildNonFiscalConfigurationForm(printer: Printers | undefined) {

    const type = printer?.type as PrinterType;
    let configuration = (type && DEFAULT_CONFIGS[type]) ? DEFAULT_CONFIGS[type] : {};

    if(printer?.configuration) {
      try{
        configuration = JSON.parse(printer.configuration);
      } catch(error) {
        configuration = {};
      }
    }

    const availableRooms = this.printerModuleService.rooms;

    let roomsArray = new CustomFormArray(
      availableRooms.map(room => new CustomFormGroup({
        id: new CustomFormControl(room.id),
        name: new CustomFormControl(room.name),
        checked: new CustomFormControl(false)
      }))
    );

    roomsArray.push(new CustomFormGroup({
        id: new CustomFormControl('take_away'),
        name: new CustomFormControl(this.translateService.instant('PRINTERS.DETAIL.TAKE_AWAY')),
        checked: new CustomFormControl(false)
      })
    );
    roomsArray.push(new CustomFormGroup({
        id: new CustomFormControl('delivery'),
        name: new CustomFormControl(this.translateService.instant('PRINTERS.DETAIL.DELIVERY')),
        checked: new CustomFormControl(false)
      })
    );
    roomsArray.push(new CustomFormGroup({
        id: new CustomFormControl('no_rooms'),
        name: new CustomFormControl(this.translateService.instant('PRINTERS.DETAIL.NO_ROOMS')),
        checked: new CustomFormControl(false)
      })
    );

    const availableCashregisters = this.printerModuleService.printersList
      .filter((p) => (['fiscal', 'rt', 'receipt'].includes(p.type) && p.id !== printer?.id));

    let cashregistersArray = new CustomFormArray(
      availableCashregisters.map(cash => new CustomFormGroup({
        id: new CustomFormControl(cash.id),
        name: new CustomFormControl(cash.name),
        checked: new CustomFormControl(false)
      }))
    );

    const availableOtherPrinters = this.printerModuleService.printersList
      .filter((p) => (p.type === 'nonfiscal' && p.id !== printer?.id));

    let otherPrintersArray = new CustomFormArray(
      availableOtherPrinters.map(print => new CustomFormGroup({
        id: new CustomFormControl(print.id),
        name: new CustomFormControl(print.name),
        checked: new CustomFormControl(false),
        inline: new CustomFormControl({ value: false, disabled: true })
      }))
    );

    const availableItems = this.printerModuleService.selection()?.items || [];

    let itemsArray = new CustomFormArray(
      availableItems?.map(item => new CustomFormGroup({
        id: new CustomFormControl(item.id),
        name: new CustomFormControl(item.name),
        checked: new CustomFormControl({ value: true, disabled: true })
      }))
    );

    this.markChecked(roomsArray, configuration.rooms);
    this.markChecked(cashregistersArray, configuration.cashregisters);
    this.markChecked(otherPrintersArray, configuration.other_printers);

    const allRoomsSelected = availableRooms.length > 0 && 
                        roomsArray.controls.every(room => room.get('checked')?.value);

    const allCashregistersSelected = availableCashregisters.length > 0 && 
                        cashregistersArray.controls.every(room => room.get('checked')?.value);

    const allOtherPrintersSelected = availableOtherPrinters.length > 0 && 
                        otherPrintersArray.controls.every(room => room.get('checked')?.value);

    const group = new CustomFormGroup<NonFiscalFieldsForm>({
      _allRooms: new CustomFormControl(allRoomsSelected),
      rooms: roomsArray,
      _allCashregisters: new CustomFormControl(allCashregistersSelected),
      cashregisters: cashregistersArray,
      _allOtherPrinters: new CustomFormControl(allOtherPrintersSelected),
      otherPrinters: otherPrintersArray,
      items: itemsArray
    } as NonFiscalFieldsForm);

    return group;
  }

  private markChecked(formArray: CustomFormArray<any>, selectedItems: any[] | undefined) {
    if (!selectedItems || selectedItems.length === 0) return;

    const selectedMap = new Map<any, any>();
    selectedItems.forEach(item => {
      if (typeof item === 'object' && item !== null) {
        selectedMap.set(item.id, item);
      } else {
        selectedMap.set(item, true);
      }
    });

    formArray.controls.forEach(control => {
      const id = control.get('id')?.value;
      if (selectedMap.has(id)) {
        control.get('checked')?.setValue(true);
        
        if (control.get('inline') && selectedMap.get(id).inline !== undefined) {
          control.get('inline')?.setValue(selectedMap.get(id).inline);
          control.enable();
        }
      }
    });
  }
  
  preparePrinterForSave(): Partial<Printers> {
    const printerData = {
      ...structuredClone(this.printerModuleService.selection()?.printer ?? {}),
      ...this.settingsGroup?.getRawValue(),
    };

    // Categorie
    printerData.categories = (this.categoriesArray?.value || [])
      .filter(c => c.checked)
      .map(({ checked, ...category }) => category);

    // keep the stored keys that have no form control
    const storedConfiguration = this.parseConfiguration(printerData.configuration);

    // Configurazione in base al tipo
    if (this.printerType?.value === 'nonfiscal') {
      const config = {
        ...storedConfiguration,
        ...this.configurationGroup?.value,
        cashregisters: (this.cashregistersArray?.value || []).filter(p => p.checked).map(p => p.id),
        other_printers: (this.otherPrintersArray?.value || []).filter(p => p.checked).map(({ id, inline }) => ({ id, inline })),
        rooms: (this.roomsArray?.value || []).filter(r => r.checked).map(r => r.id),
      };
      printerData.configuration = JSON.stringify(config);
    } else {
      // Pulizia campi
      if (['fiscal', 'receipt', 'rt', 'kds'].includes(printerData.type)) {
        ['ip_address', 'subnet_mask', 'gateway'].forEach((key) => {
          const typedKey = key as keyof Printers;
          if (printerData[typedKey] === "" && typedKey !== 'id') printerData[typedKey] = null;
        });
      }

      // Serializzazione configurazione standard
      const { header_font_size_1, ...cleanConfig } = { ...storedConfiguration, ...this.configurationGroup?.value };
      printerData.configuration = Object.keys(cleanConfig).length ? JSON.stringify(cleanConfig) : null;
    }

    return printerData;
  }


  //
  get operationGroup(): CustomFormGroup<PrintersFieldForm> {
    return this.printerForm.controls.operationForm;
  }

  //
  get settingsGroup(): CustomFormGroup<PrintersFieldForm> {
    return this.printerForm.controls.settingsForm;
  }

  get printerName() {
    return this.settingsGroup.controls.name;
  }

  get printerType() {
    return this.settingsGroup.controls.type;
  }

  get printerId() {
    return this.settingsGroup.controls.id;
  }

  get ipAddress() {
    return this.settingsGroup.controls.ip_address;
  }

  get driver() {
    return this.settingsGroup.controls.driver;
  }

  get fiscalProvider() {
    return this.settingsGroup.controls.fiscal_provider;
  }

  get ssl() {
    return this.settingsGroup.controls.ssl;
  }

  get printerNumber() {
    return this.settingsGroup.controls.printer_number;
  }

  get columns() {
    return this.settingsGroup.controls.columns;
  }

  get topSpace() {
    return this.settingsGroup.controls.top_space;
  }

  get bottomSpace() {
    return this.settingsGroup.controls.bottom_space;
  }

  get enableBuzzer() {
    return this.settingsGroup.controls.enable_buzzer;
  }

  get configurationPending() {
    return this.settingsGroup.controls.configuration_pending;
  }

  get printEinvoice() {
    return this.settingsGroup.controls.print_e_invoice;
  }

  get printFiscalReceipt() {
    return this.settingsGroup.controls.print_fiscal_receipt;
  }

  get printGenericDocument() {
    return this.settingsGroup.controls.print_generic_document;
  }

  get printGenericInvoice() {
    return this.settingsGroup.controls.print_generic_invoice;
  }

  get printGenericReceipt() {
    return this.settingsGroup.controls.print_generic_receipt;
  }

  get printInvoice() {
    return this.settingsGroup.controls.print_invoice;
  }

  get printReceipt() {
    return this.settingsGroup.controls.print_receipt;
  }

  get printReceiptInvoice() {
    return this.settingsGroup.controls.print_receipt_invoice;
  }

  get printShippingInvoice() {
    return this.settingsGroup.controls.print_shipping_invoice;
  }

  get printSummaryEnrc() {
    return this.settingsGroup.controls.print_summary_e_nrc;
  }

  get printSummaryErc() {
    return this.settingsGroup.controls.print_summary_e_rc;
  }

  get printSummaryInvoice() {
    return this.settingsGroup.controls.print_summary_invoice;
  }

  //
  get configurationGroup(): CustomFormGroup<ConfigurationFieldForm> {
    return this.printerForm.controls.configurationForm;
  }

  get codepageMapping() {
    return this.configurationGroup.controls.codepage_mapping;
  }

  get lineSpacing() {
    return this.configurationGroup.controls.line_spacing;
  }

  get itemSpacing() {
    return this.configurationGroup.controls.item_spacing;
  }

  get itemsAlign() {
    return this.configurationGroup.controls.items_align;
  }

  get itemHideBorder() {
    return this.configurationGroup.controls.item_hide_border;
  }

  get itemHideBrackets() {
    return this.configurationGroup.controls.item_hide_brackets;
  }

  get splitOrdersByExit() {
    return this.configurationGroup.controls.split_orders_by_exit;
  }

  get addPricesToOrder() {
    return this.configurationGroup.controls.add_prices_to_order;
  }

  get mergeSameItems() {
    return this.configurationGroup.controls.merge_same_items;
  }

  get headerOrderCategories() {
    return this.configurationGroup.controls.header_order_categories;
  }

  get footerOrderCategories() {
    return this.configurationGroup.controls.footer_order_categories;
  }

  get headerOrderNumber() {
    return this.configurationGroup.controls.header_order_number;
  }

  get headerOperatorName() {
    return this.configurationGroup.controls.header_operator_name;
  }

  get headerDate() {
    return this.configurationGroup.controls.header_date;
  }

  get headerTime() {
    return this.configurationGroup.controls.header_time;
  }

  get headerPrintType() {
    return this.configurationGroup.controls.header_print_type;
  }

  get headerRoomName() {
    return this.configurationGroup.controls.header_room_name;
  }

  get headerTableName() {
    return this.configurationGroup.controls.header_table_name;
  }

  get headerCovers() {
    return this.configurationGroup.controls.header_covers;
  }

  get headerDeliveryInfo() {
    return this.configurationGroup.controls.header_delivery_info;
  }

  get headerOrderName() {
    return this.configurationGroup.controls.header_order_name;
  }

  get headerCustomerInfo() {
    return this.configurationGroup.controls.header_customer_info;
  }

  get headerOrderNotes() {
    return this.configurationGroup.controls.header_order_notes;
  }

  get headerTotalPieces() {
    return this.configurationGroup.controls.header_total_pieces;
  }

  get headerFinalAmount() {
    return this.configurationGroup.controls.header_final_amount;
  }

  get itemFontSize() {
    return this.configurationGroup.controls.item_font_size;
  }

  get variationFontSize() {
    return this.configurationGroup.controls.variation_font_size;
  }

  get otherPrintersFontSize() {
    return this.configurationGroup.controls.other_printers_font_size;
  }

  get itemsGroupBy() {
    return this.configurationGroup.controls.items_group_by;
  }

  get headerPrinterCategories() {
    return this.configurationGroup.controls.header_printer_categories;
  }

  get footerOrderNumber() {
    return this.configurationGroup.controls.footer_order_number;
  }

  get footerOperatorName() {
    return this.configurationGroup.controls.footer_operator_name;
  }

  get footerDate() {
    return this.configurationGroup.controls.footer_date;
  }

  get footerTime() {
    return this.configurationGroup.controls.footer_time;
  }

  get footerPrintType() {
    return this.configurationGroup.controls.footer_print_type;
  }

  get footerRoomName() {
    return this.configurationGroup.controls.footer_room_name;
  }

  get footerTableName() {
    return this.configurationGroup.controls.footer_table_name;
  }

  get footerCovers() {
    return this.configurationGroup.controls.footer_covers;
  }

  get footerDeliveryInfo() {
    return this.configurationGroup.controls.footer_delivery_info;
  }

  get footerOrderName() {
    return this.configurationGroup.controls.footer_order_name;
  }

  get footerCustomerInfo() {
    return this.configurationGroup.controls.footer_customer_info;
  }

  get footerOrderNotes() {
    return this.configurationGroup.controls.footer_order_notes;
  }

  get footerTotalPieces() {
    return this.configurationGroup.controls.footer_total_pieces;
  }

  get footerFinalAmount() {
    return this.configurationGroup.controls.footer_final_amount;
  }

  get printOtherCategories() {
    return this.configurationGroup.controls.print_other_categories;
  }

  get splitStubsByCategory() {
    return this.configurationGroup.controls.split_stubs_by_category;
  }

  get fromCashregistersOnly() {
    return this.configurationGroup.controls.from_cashregisters_only;
  }

  get progressivePrefix() {
    return this.configurationGroup.controls.progressive_prefix;
  }

  //
  get categoriesGroup(): CustomFormGroup<CategoriesFieldsForm> {
    return this.printerForm.controls.categoriesForm;
  }

  get allCategories() {
    return this.categoriesGroup.controls._allCategories;
  }

  get categoriesArray() {
    return this.categoriesGroup.controls.categories;
  }

  //
  get nonFiscalConfigurationGroup(): CustomFormGroup<NonFiscalFieldsForm> {
    return this.printerForm.controls.nonFiscalConfiguration;
  }

  get allRooms() {
    return this.nonFiscalConfigurationGroup.controls._allRooms;
  }

  get roomsArray() {
    return this.nonFiscalConfigurationGroup.controls.rooms;
  }

  get allCashregisters() {
    return this.nonFiscalConfigurationGroup.controls._allCashregisters;
  }

  get cashregistersArray() {
    return this.nonFiscalConfigurationGroup.controls.cashregisters;
  }

  get allOtherPrinters() {
    return this.nonFiscalConfigurationGroup.controls._allOtherPrinters;
  }

  get otherPrintersArray() {
    return this.nonFiscalConfigurationGroup.controls.otherPrinters;
  }

  get itemsArray() {
    return this.nonFiscalConfigurationGroup.controls.items;
  }

  //
  get fiscalGeneralSettingsGroup(): CustomFormGroup<FiscalPrinterForm> {
    return this.printerForm.controls.fiscalGeneralSettings;
  }

  get displayMessage() {
    return this.fiscalGeneralSettingsGroup.controls.display_message;
  }

  get sellerPrefix() {
    return this.fiscalGeneralSettingsGroup.controls.seller_prefix;
  }

  get tail() {
    return this.fiscalGeneralSettingsGroup.controls.tail;
  }

  get qrCodeMessage() {
    return this.fiscalGeneralSettingsGroup.controls.qr_code_message;
  }

  get qrCodeUrl() {
    return this.fiscalGeneralSettingsGroup.controls.qr_code_url;
  }
}
